/**
 * CurrencyContext — provides the active company currency across the entire app.
 * Reads default_currency from /api/settings (cached in localStorage under
 * 'company_currency'). Falls back to AED (UAE default).
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { getCurrencySymbol } from '../utils/currency';

const CurrencyContext = createContext({
  currency: 'AED',
  currencySymbol: 'د.إ',
  getCurrencySymbol,
  refresh: () => {},
});

const CACHE_KEY = 'company_currency';
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState(
    () => localStorage.getItem(CACHE_KEY) || 'AED'
  );
  const [currencySymbol, setCurrencySymbol] = useState(
    () => getCurrencySymbol(localStorage.getItem(CACHE_KEY) || 'AED')
  );

  const applyCode = (code) => {
    const c = code || 'AED';
    setCurrency(c);
    setCurrencySymbol(getCurrencySymbol(c));
    localStorage.setItem(CACHE_KEY, c);
  };

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await axios.get(`${API_URL}/api/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      applyCode(res.data?.default_currency || 'AED');
    } catch {
      // keep cached / default
    }
  };

  useEffect(() => {
    fetchSettings();
    // Re-fetch when token changes (e.g. login/logout)
    const handleStorage = (e) => {
      if (e.key === 'token') fetchSettings();
      if (e.key === CACHE_KEY && e.newValue) applyCode(e.newValue);
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <CurrencyContext.Provider
      value={{ currency, currencySymbol, getCurrencySymbol, refresh: fetchSettings }}
    >
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => useContext(CurrencyContext);
